/**
 * Get Usercentrics services from the window object
 */
export const getUsercentricsServices = () => {
  return (window as any)?.__ucCmp?.cmpController?.dps?.services ?? {};
};

/**
 * Debounce function to limit the execution rate of a given function.
 */
export const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

/**
 * Waits for the __ucCmp object to be available on the window.
 */
export const waitForApi = async (): Promise<void> => {
    return new Promise((resolve) => {
        const isServicesAvailable = () => {
            return typeof (window as any).__ucCmp !== "undefined" && typeof (window as any).__ucCmp?.cmpController?.dps?.services === "object";
        };

        if (isServicesAvailable()) {
            resolve();
        } else {
            const checkInterval = setInterval(() => {
                if (isServicesAvailable()) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);
        }
    });
};

/**
 * Force re-render of iframes by removing and re-attaching them to the DOM.
 */
export const reRenderIframes = (iframes: NodeListOf<Element>) => {
    iframes.forEach((iframe) => {
        const parent = iframe.parentNode;
        if (parent) {
            const nextSibling = iframe.nextSibling;
            parent.removeChild(iframe);
            if (nextSibling) {
                parent.insertBefore(iframe, nextSibling);
            } else {
                parent.appendChild(iframe);
            }
        }
    });
};

/**
 * Applies block elements logic for a given service and triggers re-render for associated iframes.
 */
export const applyBlockElements = (service: { name: string; selector: string; serviceId: string }) => {
    const iframes = document.querySelectorAll(service.selector);
    if (iframes.length > 0) {
        if (typeof (window as any).uc !== "undefined" && typeof (window as any).uc.blockElements === "function") {
            (window as any).uc.blockElements({
                [service.serviceId]: service.selector,
            });
            reRenderIframes(iframes);
        } else {
            console.error("Usercentrics blockElements function is not available.");
        }
    } else {
        console.warn(`No iframes found for service: "${service.name}"`);
    }
};

/**
 * Generates fallback consent for services when status resolution fails.
 */
export const generateFallbackConsent = (
    services: { serviceId: string }[]
): { [serviceId: string]: boolean } => {
    return services.reduce((consentStatus, service) => {
        consentStatus[service.serviceId] = false;
        return consentStatus;
    }, {} as { [serviceId: string]: boolean });
};

/**
 * Sets the src attribute of an iframe, trying data-src first, then data-default-src.
 * Makes the iframe visible and returns whether the operation was successful.
 * 
 * @param iframe - The iframe element to set the src for
 * @param context - Optional context string for logging (e.g., "resize handler")
 * @returns An object with success status and the final src value
 */
export const setIframeSrc = (iframe: HTMLIFrameElement, context: string = ""): { success: boolean; src: string | null } => {
    const logPrefix = context ? `[Overlay Debug] ${context}: ` : "[Overlay Debug] ";
    let success = false;
    let finalSrc: string | null = null;

    // Try to set src from data-src first
    const dataSrc = iframe.getAttribute("data-src");
    if (dataSrc) {
        iframe.setAttribute("src", dataSrc);
        iframe.removeAttribute("data-src");
        finalSrc = dataSrc;
        success = true;
    }

    // If src is still empty, try data-default-src
    if (!finalSrc) {
        const src = iframe.getAttribute("src");
        if (!src && iframe.dataset.defaultSrc) {
            iframe.setAttribute("src", iframe.dataset.defaultSrc);
            finalSrc = iframe.dataset.defaultSrc;
            success = true;
        }
    }

    // Final check - if src is still empty, log a warning
    if (!finalSrc) {
        finalSrc = iframe.getAttribute("src");
        if (!finalSrc) {
            console.warn(`${logPrefix}Warning: Unable to set src for iframe, no source found`);
            success = false;
        } else {
            success = true;
        }
    }

    // Make the iframe visible
    iframe.style.display = "block";

    return { success, src: finalSrc };
};
